import React, { useId, useMemo, useState } from 'react';
import { useTableViewPreference } from './CardableTable';
import { LibraryViewToggle } from '../ui/LibraryViewToggle';
import UserAvatar from '../ui/UserAvatar';
import { auditActorLabel, isAuditRowUndoable } from '../data/auditLog';
import type { AuditLogRow } from '../data/auditLog';
import type { DisplayItem, FieldChange, RecordHistoryTabProps } from './RecordHistoryTab';

interface Props {
  items: DisplayItem[];
  storageKey: string;
  userNames: Record<string, string>;
  userHeadshots?: Record<string, string>;
  labelField: (key: string, row: AuditLogRow) => string;
  tableToTab: (table: string) => string;
  actionLabel: (action: string) => string;
  status: string;
  busy: boolean;
  canUndo: boolean;
  undoMutation: RecordHistoryTabProps['undoMutation'];
  renderRowAction?: RecordHistoryTabProps['renderRowAction'];
  renderFieldValue?: RecordHistoryTabProps['renderFieldValue'];
  formatFieldPreview?: RecordHistoryTabProps['formatFieldPreview'];
}

const SUBJECTS: Record<string, string> = {
  photos: 'a photo', venue_rooms: 'a room', venue_notes: 'a note',
  venue_logistics: 'logistics', venues: 'venue details', venue_contacts: 'a contact',
  venue_room_files: 'a room file', venue_room_layouts: 'a room layout',
  venue_in_house_av: 'the AV provider', venue_guidelines: 'venue guidelines',
};
const isEmpty = (value: unknown) => value == null || value === '';
function valueText(value: unknown): string {
  if (isEmpty(value)) return 'Not set';
  return typeof value === 'string' ? value : JSON.stringify(value) ?? String(value);
}
const isReference = (change: FieldChange) => /_id$/.test(change.key)
  || (change.key === 'section' && [change.before, change.after].some(value => typeof value === 'string' && /^(room|note):/.test(value)));
const previewText = (value: unknown) => {
  const text = valueText(value);
  return text.length > 220 ? `${text.slice(0, 220)}…` : text;
};

function ActivityEntry({ item, userNames, userHeadshots, labelField, tableToTab, actionLabel, canUndo, undoMutation, renderRowAction, renderFieldValue, formatFieldPreview }: Pick<Props, 'userNames' | 'userHeadshots' | 'labelField' | 'tableToTab' | 'actionLabel' | 'canUndo' | 'undoMutation' | 'renderRowAction' | 'renderFieldValue' | 'formatFieldPreview'> & { item: DisplayItem }) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const { row, changes } = item;
  const actor = auditActorLabel(row, userNames);
  const fields = changes.filter(change => !isReference(change));
  const references = changes.filter(isReference);
  const source = row.activity_source || (row.changed_by ? 'user' : 'system');
  const headshotUrl = source === 'user' && row.changed_by ? userHeadshots?.[row.changed_by] : undefined;
  const initials = source === 'ai' ? 'AI' : source === 'import' ? 'IM' : !row.changed_by ? 'SY'
    : actor.split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  const date = new Date(row.changed_at);
  const subject = SUBJECTS[row.table_name] || tableToTab(row.table_name).toLowerCase();
  const description = row.action === 'RESEARCH_STATUS' ? 'recorded a research update'
    : row.action === 'SOFT_DELETE' ? `marked ${subject} as deleted`
      : `${actionLabel(row.action).toLowerCase()} ${subject}`;
  const canExpand = changes.length > 0 || !!renderRowAction || canUndo;
  const renderComparison = (change: FieldChange) => <div className="activity-comparison" key={change.key}>
    <h4>{labelField(change.key, row)}</h4>
    <div className="activity-comparison__values" data-added={row.action === 'INSERT' || undefined}>
      {row.action !== 'INSERT' && <div><span className="activity-comparison__label">Before</span><div data-empty={isEmpty(change.before) || undefined}>{renderFieldValue ? renderFieldValue(change.before, change.key, row) : valueText(change.before)}</div></div>}
      <div><span className="activity-comparison__label">{row.action === 'INSERT' ? 'Added value' : 'After'}</span><div data-empty={isEmpty(change.after) || undefined}>{renderFieldValue ? renderFieldValue(change.after, change.key, row) : valueText(change.after)}</div></div>
    </div>
  </div>;
  return <li className="activity-entry" data-audit-id={row.id}>
    <UserAvatar className={`activity-entry__avatar activity-entry__avatar--${source}`} name={actor} src={headshotUrl} fallback={initials} />
    <div className="activity-entry__body">
      <div className="activity-entry__headline">
        <div className="activity-entry__sentence"><strong className="history-activity__actor">{actor}</strong>{' '}
          <span>{description}</span>
        </div>
        <time dateTime={row.changed_at} aria-label={date.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'long' })}>{date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</time>
      </div>
      {row.record_label && <div className="activity-entry__record">{row.record_label}</div>}
      {fields.length > 0 && <div className="activity-entry__preview">
        {fields.slice(0, 2).map(change => <div className="activity-preview" key={change.key}>
          {labelField(change.key, row) !== row.record_label && <span className="activity-preview__label">{labelField(change.key, row)}</span>}
          <span className="activity-preview__value">
            {!formatFieldPreview && !isEmpty(change.before) && valueText(change.before).length < 65 && valueText(change.after).length < 65 && <><span className="activity-preview__old">{valueText(change.before)}</span><span className="activity-preview__arrow" aria-label="changed to"> → </span></>}
            {isEmpty(change.after) ? <span className="activity-preview__removed">Value cleared</span> : formatFieldPreview ? formatFieldPreview(change.after, change.key) : previewText(change.after)}
          </span>
        </div>)}
      </div>}
      {changes.length === 0 && row.action !== 'DELETE' && <p className="activity-entry__unavailable">Field details weren’t retained.</p>}
      {canExpand && <button type="button" className="activity-entry__disclosure" aria-expanded={expanded} aria-controls={detailsId} onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Hide details' : fields.length ? `View ${fields.length === 1 ? 'change' : `${fields.length} changes`}` : 'View details'}
        <svg aria-hidden="true" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d={expanded ? 'm4 10 4-4 4 4' : 'm4 6 4 4 4-4'} /></svg>
      </button>}
      <div id={detailsId} className="activity-entry__details" hidden={!expanded}>{expanded && <>
        {fields.map(renderComparison)}
        {references.length > 0 && <details className="activity-references"><summary>Record references ({references.length})</summary>{references.map(renderComparison)}</details>}
        <div className="activity-entry__detail-footer"><time dateTime={row.changed_at}>{date.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'long' })}</time>
          {renderRowAction ? renderRowAction(item) : canUndo && (isAuditRowUndoable(row)
            ? <button type="button" className="btn small" disabled={undoMutation.isPending} onClick={() => undoMutation.mutate(row)}>{undoMutation.isPending ? 'Undoing…' : 'Undo this change'}</button>
            : <span>Undo unavailable for this retained entry.</span>)}
        </div>
      </>}</div>
    </div>
  </li>;
}

export default function VenueActivityFeed({ items, storageKey, status, busy, ...props }: Props) {
  const [view, setView] = useTableViewPreference(storageKey, 'list');
  const groups = useMemo(() => {
    const days = new Map<string, DisplayItem[]>();
    for (const item of items) {
      const day = new Date(item.row.changed_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
      const group = days.get(day) || [];
      group.push(item);
      days.set(day, group);
    }
    return Array.from(days);
  }, [items]);
  const timezone = Intl.DateTimeFormat(undefined, { timeZoneName: 'shortGeneric' }).formatToParts().find(part => part.type === 'timeZoneName')?.value;
  return <div className="venue-activity" data-view={view}>
    <div className="venue-activity__controls">
      <span role="status" aria-live="polite">{status}</span>
      <div className="venue-activity__view"><span className="venue-activity__timezone">Times in {timezone}</span><LibraryViewToggle value={view} options={[{ value: 'list', label: 'List' }, { value: 'grid', label: 'Grid' }]} onChange={setView} ariaLabel="Change history view" /></div>
    </div>
    <div className="venue-activity__days" aria-label="Change history" aria-busy={busy}>
      {groups.map(([day, entries]) => <section className="activity-day" key={day}>
        <h3 className="activity-day__heading">{day}</h3>
        <ol className="activity-day__entries">{entries.map(item => <ActivityEntry key={item.row.id} item={item} {...props} />)}</ol>
      </section>)}
    </div>
  </div>;
}
